import { PaperPlaneTilt, X } from '@phosphor-icons/react';
import { type FC, type KeyboardEvent, useCallback, useState } from 'react';
import type { WidgetContext } from '@providers/QuickHelp/types';
import styles from './QuickHelpPanel.module.css';

interface Props {
    onSend: (message: string) => void;
    isStreaming: boolean;
    attachedContext: WidgetContext | null;
    onRemoveContext: () => void;
}

const QuickHelpInput: FC<Props> = ({
    onSend,
    isStreaming,
    attachedContext,
    onRemoveContext,
}) => {
    const [value, setValue] = useState('');

    const handleSend = useCallback(() => {
        const trimmed = value.trim();
        if (!trimmed || isStreaming) return;
        onSend(trimmed);
        setValue('');
    }, [value, isStreaming, onSend]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    return (
        <div className={styles.inputArea}>
            {attachedContext && (
                <div className={styles.contextPill} style={{ marginBottom: 8 }}>
                    <span className={styles.contextPillLabel}>{attachedContext.label}</span>
                    <button
                        type="button"
                        className={styles.contextPillRemove}
                        onClick={onRemoveContext}
                        aria-label="Remove context"
                    >
                        <X size={10} weight="bold" />
                    </button>
                </div>
            )}

            <div className={`${styles.inputWrapper} ${isStreaming ? styles.inputWrapperStreaming : ''}`}>
                <textarea
                    value={value}
                    onChange={(e) => setValue(e.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask anything…"
                    disabled={isStreaming}
                    rows={1}
                    style={{
                        flex: 1,
                        resize: 'none',
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        fontSize: 13,
                        lineHeight: 1.5,
                        color: '#111827',
                        fontFamily: 'inherit',
                        maxHeight: 120,
                        overflowY: 'auto',
                        padding: 0,
                    }}
                    onInput={(e) => {
                        const el = e.currentTarget;
                        el.style.height = 'auto';
                        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
                    }}
                />

                <button
                    type="button"
                    className={styles.sendButton}
                    onClick={handleSend}
                    disabled={!value.trim() || isStreaming}
                    aria-label="Send message"
                >
                    <PaperPlaneTilt size={14} weight="fill" />
                </button>
            </div>
        </div>
    );
};

export default QuickHelpInput;
